import type { ReactNode } from 'react';
import { Logo } from './ui';

/* Rechtliche Pflichttexte für Mach's (machs.app)
 *
 * Grundlage sind die Texte von Quitt (gleicher Betreiber, gleiches Hosting-Muster),
 * angepasst an die Funktionen von Mach's. Vor dem Start mit zahlenden Kunden
 * anwaltlich prüfen lassen, insbesondere AVV (Subunternehmer, TOMs) und AGB.
 * Kontaktdaten stehen zentral in OPERATOR.
 */

export type LegalPage = 'impressum' | 'datenschutz' | 'agb' | 'avv' | 'support' | 'konto-loeschen';

const OPERATOR = {
  name: 'sütti.it – Timo Sütterlin',
  street: 'Spielweg 31',
  city: '79244 Münstertal',
  country: 'Deutschland',
  email: 'kontakt@suetti.de',
  web: 'sütti.it',
  webUrl: 'https://suetti.it',
  ustId: 'DE255217062',
} as const;

const UPDATED = '17. September 2026';
const SITE = 'machs.app';

const Mail = () => <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>;
const Ext = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

export const LEGAL_TITLES: Record<LegalPage, string> = {
  impressum: 'Impressum',
  datenschutz: 'Datenschutzerklärung',
  agb: 'Allgemeine Geschäftsbedingungen',
  avv: 'Auftragsverarbeitungsvertrag',
  support: 'Hilfe und Kontakt',
  'konto-loeschen': 'Konto und Daten löschen',
};

export function LegalView({ page, onHome, onNavigate }: { page: LegalPage; onHome: () => void; onNavigate: (p: LegalPage) => void }) {
  return (
    <div className="landing">
      <header className="lp-head">
        <button onClick={onHome} aria-label="Zur Startseite"><Logo size={34} claim /></button>
        <nav className="flex items-center gap-2">
          <button className="btn btn-ghost h-10 px-4" onClick={onHome}>Zur Startseite</button>
        </nav>
      </header>
      <article className="legal">
        {page === 'impressum' && <Impressum />}
        {page === 'datenschutz' && <Datenschutz />}
        {page === 'agb' && <Agb />}
        {page === 'avv' && <Avv />}
        {page === 'support' && <Support />}
        {page === 'konto-loeschen' && <KontoLoeschen />}
      </article>
      <LegalFooter onNavigate={onNavigate} />
    </div>
  );
}

export function LegalFooter({ onNavigate, extra }: { onNavigate: (p: LegalPage) => void; extra?: ReactNode }) {
  const link = (p: LegalPage, label: string) => (
    <a
      href={`/${p}`}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(p);
      }}
    >
      {label}
    </a>
  );
  return (
    <footer className="lp-foot">
      <span>Mach's ist ein Produkt von <Ext href={OPERATOR.webUrl}>{OPERATOR.web}</Ext></span>
      <nav className="flex flex-wrap gap-x-5 gap-y-2">
        {link('support', 'Hilfe')}
        {link('impressum', 'Impressum')}
        {link('datenschutz', 'Datenschutz')}
        {link('agb', 'AGB')}
        {link('avv', 'AVV')}
        {link('konto-loeschen', 'Konto löschen')}
        {extra}
      </nav>
    </footer>
  );
}

function Impressum() {
  return (
    <>
      <h1>Impressum</h1>
      <p><strong>Angaben gemäß § 5 DDG</strong></p>
      <p>{OPERATOR.name}<br />{OPERATOR.street}<br />{OPERATOR.city}</p>
      <p><strong>Kontakt</strong><br />E-Mail: <Mail /><br />Web: <Ext href={OPERATOR.webUrl}>{OPERATOR.web}</Ext></p>
      <p><strong>Umsatzsteuer-ID</strong><br />Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: {OPERATOR.ustId}</p>
      <p><strong>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</strong><br />{OPERATOR.name}, Anschrift wie oben</p>
      <p>
        <strong>Streitschlichtung</strong><br />
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </>
  );
}

function Datenschutz() {
  return (
    <>
      <h1>Datenschutzerklärung</h1>
      <p className="legal-date">Stand: {UPDATED}</p>

      <h2>1. Verantwortlicher</h2>
      <p>{OPERATOR.name}<br />{OPERATOR.street}<br />{OPERATOR.city}, {OPERATOR.country}<br />E-Mail: <Mail /></p>

      <h2>2. Geltungsbereich</h2>
      <p>
        Diese Datenschutzerklärung gilt für die Webanwendung Mach's unter {SITE} (einschließlich der Übergangsadresse
        machs.suetti.it), die Apps für iPhone, iPad und Android sowie die Freigabe-Ansicht für externe Personen. Sie
        informiert über Art, Umfang und Zweck der Verarbeitung personenbezogener Daten gemäß Art. 13 und 14 DSGVO.
      </p>

      <h2>3. Server-Logfiles</h2>
      <p>Beim Aufruf erhebt der Server automatisch Daten, die Browser oder App übermitteln:</p>
      <ul>
        <li>IP-Adresse</li>
        <li>Datum und Uhrzeit des Zugriffs</li>
        <li>Aufgerufene Adresse und HTTP-Statuscode</li>
        <li>Browser- bzw. App-Typ und Betriebssystem (User-Agent)</li>
      </ul>
      <p>
        <strong>Zweck:</strong> stabiler und sicherer Betrieb, Abwehr von Angriffen.<br />
        <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO.<br />
        <strong>Speicherdauer:</strong> höchstens 30 Tage.
      </p>

      <h2>4. Registrierung und Nutzerkonto</h2>
      <p>
        Bei der Registrierung verarbeiten wir Name, E-Mail-Adresse, Passwort (nur als bcrypt-Hash, nie im Klartext),
        den Namen des Teams sowie optional ein Profilbild. Innerhalb eines Teams sehen die anderen Mitglieder Name,
        E-Mail-Adresse und Profilbild.
      </p>
      <p>
        <strong>Zweck:</strong> Bereitstellung der Anwendung und Anmeldung.<br />
        <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).<br />
        <strong>Speicherdauer:</strong> bis zur Löschung des Kontos, danach Löschung innerhalb von 30 Tagen, soweit keine
        gesetzliche Aufbewahrungspflicht besteht.
      </p>

      <h2>5. Anmeldung mit Google oder Apple</h2>
      <p>
        Optional ist die Anmeldung mit einem Google- oder Apple-Konto möglich. Dabei erhalten wir E-Mail-Adresse, Name und
        eine eindeutige Kennung des Kontos. Bei „Mit Apple anmelden“ kann die E-Mail-Adresse eine von Apple
        weitergeleitete Adresse sein. Inhalte aus Google- oder Apple-Diensten werden nicht ausgelesen.
      </p>
      <p>
        <strong>Anbieter:</strong> Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland; Apple
        Distribution International Ltd., Hollyhill Industrial Estate, Cork, Irland.<br />
        <strong>Drittlandtransfer:</strong> Eine Verarbeitung in den USA ist möglich. Beide Anbieter sind nach dem
        EU-US Data Privacy Framework zertifiziert.<br />
        <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), jederzeit mit Wirkung für die
        Zukunft widerrufbar per E-Mail an <Mail />.
      </p>

      <h2>6. Inhalte in Mach's</h2>
      <p>
        Mach's verarbeitet die Inhalte, die Nutzer anlegen: Geschäfte, Projekte, Aufgaben, Nachrichten, Fotos, Videos,
        Sprachnachrichten, Dateien, Links, Bildschirmaufnahmen, Meilensteine, Zuständigkeiten und Termine. Zu Links ruft
        der Server Titel, Beschreibung und Vorschaubild der verlinkten Seite ab und speichert das Bild selbst, damit
        beim Anzeigen keine fremde Seite aufgerufen wird.
      </p>
      <p>
        <strong>Standort:</strong> Wer seinen Standort teilt (nur auf ausdrücklichen Klick, der Browser fragt zusätzlich
        nach), sendet die Koordinaten als Nachricht in die Aufgabe. Für Adresse und Kartenausschnitt übermittelt der
        Server die Koordinaten – ohne Bezug zur Person – an die OpenStreetMap Foundation (Nominatim und Kartenkacheln,
        Vereinigtes Königreich, Angemessenheitsbeschluss der EU-Kommission).
      </p>
      <p>
        <strong>Rolle:</strong> Für diese Inhalte sind wir Auftragsverarbeiter nach Art. 28 DSGVO. Verantwortlich ist das
        jeweilige Team (Kunde). Der <a href="/avv">Auftragsverarbeitungsvertrag</a> gilt mit den AGB als geschlossen.
      </p>

      <h2>7. KI-Auswertung mit eigenem Schlüssel</h2>
      <p>
        Teams können für ihre Geschäfte einen eigenen API-Schlüssel eines KI-Anbieters hinterlegen (OpenAI, Anthropic oder
        Google Gemini). Dann werden Sprachnachrichten, Videos, Bilder und Links an diesen Anbieter übermittelt, um
        Transkripte, Zusammenfassungen und Inhaltspunkte zu erstellen. Ohne hinterlegten Schlüssel findet keine
        KI-Verarbeitung statt. Die Schlüssel werden verschlüsselt (AES-256-GCM) gespeichert.
      </p>
      <p>
        Die Übermittlung erfolgt im Auftrag und nach Weisung des Teams, das den Schlüssel hinterlegt und mit dem
        jeweiligen Anbieter einen eigenen Vertrag hat. Die Anbieter können Daten in den USA verarbeiten; maßgeblich sind
        deren Datenschutz- und Auftragsverarbeitungsbedingungen.
      </p>

      <h2>8. Verbindung mit KI-Assistenten und anderen Systemen</h2>
      <p>
        Nutzer können Claude, ChatGPT oder andere Assistenten mit ihrem Konto verbinden. Der Assistent erhält dann nach
        ausdrücklicher Freigabe im Anmeldedialog Zugriff auf die Daten des Teams, soweit der Nutzer selbst Zugriff hat.
        Verbindungen lassen sich in den Einstellungen jederzeit trennen. Für die Verarbeitung beim Anbieter des
        Assistenten gelten dessen Bedingungen.
      </p>
      <p>
        Inhaber und Admins eines Teams können außerdem API-Schlüssel für andere Systeme anlegen (Zugriff wie die Person,
        die den Schlüssel anlegt) und Webhooks einrichten. Ein Webhook übermittelt bei den ausgewählten Ereignissen
        Inhalte wie Aufgabentitel, Nachrichtentexte, Namen der Beteiligten und Transkripte an die vom Team angegebene
        Adresse. Diese Übermittlung erfolgt auf Weisung des Teams; für die Verarbeitung beim Empfänger ist das Team
        verantwortlich. Schlüssel und Webhooks lassen sich jederzeit widerrufen bzw. löschen, das Protokoll der Sendungen
        wird nach höchstens 30 Tagen gelöscht.
      </p>
      <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO.</p>

      <h2>9. Freigaben für externe Personen</h2>
      <p>
        Über einen Freigabe-Link können Externe ohne Konto in einer Aufgabe oder einem Projekt mitarbeiten. Dabei
        speichern wir den angegebenen Namen, Beitrittszeitpunkt, letzte Aktivität sowie zur Nachvollziehbarkeit ein
        Zugriffsprotokoll mit IP-Adresse und User-Agent. Die Beiträge sind für alle Beteiligten sichtbar und als „extern“
        gekennzeichnet.
      </p>
      <p>
        <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (Nachweis und Missbrauchsschutz) bzw. lit. b.<br />
        <strong>Speicherdauer:</strong> Zugriffsprotokolle höchstens 12 Monate; Name und Beiträge, solange die Aufgabe
        besteht.
      </p>

      <h2>10. Apps für iPhone, iPad und Android</h2>
      <p>Die Apps greifen nur nach Ihrer Freigabe und nur für die jeweilige Funktion auf Folgendes zu:</p>
      <ul>
        <li>Kamera und Fotomediathek: Fotos und Videos an Aufgaben anhängen</li>
        <li>Mikrofon: Sprachnachrichten und Ton zu Bildschirmaufnahmen</li>
        <li>Bildschirmaufnahme: nur nach Start durch Sie, beendet durch Sie</li>
        <li>Teilen-Funktion: Inhalte, die Sie aus anderen Apps an Mach's senden</li>
        <li>Näherungssensor: Umschalten auf die Hörmuschel beim Abspielen von Sprachnachrichten (keine Speicherung)</li>
      </ul>
      <p>
        Für die Offline-Nutzung werden Daten und noch nicht übertragene Dateien lokal auf dem Gerät gespeichert und nach
        der Übertragung abgeglichen. Die Apps enthalten keine Werbung, kein Tracking und keine Analyse-SDKs.
      </p>

      <h2>11. Push-Benachrichtigungen</h2>
      <p>
        Push-Benachrichtigungen sind standardmäßig ausgeschaltet. Schalten Sie sie ein, wird ein Geräte-Token über den
        Apple Push Notification Service bzw. Firebase Cloud Messaging (Google) genutzt. Rechtsgrundlage ist Ihre
        Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), die Sie in den Einstellungen jederzeit widerrufen können.
      </p>
      <p>
        Im Browser bzw. in der installierten Web-App schalten Sie Benachrichtigungen je Gerät unter
        „Benachrichtigungen“ ein. Wir speichern dafür die vom Browser vergebene Zustelladresse und deren Schlüssel sowie
        Ihre Auswahl, worüber Sie benachrichtigt werden wollen. Die Zustellung läuft über den Push-Dienst Ihres
        Browsers (etwa Google, Apple oder Mozilla); der Inhalt – Titel der Aufgabe und ein Auszug der Nachricht – ist
        dabei Ende-zu-Ende verschlüsselt und nur auf Ihrem Gerät lesbar. Beim Ausschalten, beim Abmelden und beim Löschen
        des Kontos wird die Zustelladresse gelöscht.
      </p>

      <h2>12. Zahlungsabwicklung über Stripe</h2>
      <p>
        Kostenpflichtige Pläne werden ausschließlich im Web über Stripe Payments Europe Ltd., 1 Grand Canal Street Lower,
        Dublin 2, Irland, abgewickelt. Zahlungsdaten geben Sie direkt bei Stripe ein; wir erhalten Kunden-ID, Status des
        Abos und Rechnungsbeträge. Stripe verarbeitet Daten auch in den USA (Standardvertragsklauseln, Data Privacy
        Framework). Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.{' '}
        <Ext href="https://stripe.com/de/privacy">stripe.com/de/privacy</Ext>
      </p>

      <h2>13. Cookies und lokaler Speicher</h2>
      <p>
        Mach's setzt keine Tracking-Cookies und keine Analyse-Tools ein. Im lokalen Speicher des Browsers bzw. der App
        liegen nur das Anmelde-Token, lokale Kopien Ihrer Daten für die Offline-Nutzung und Einstellungen (z. B. hell
        oder dunkel). Das ist für den Dienst unbedingt erforderlich (§ 25 Abs. 2 Nr. 2 TDDDG).
      </p>
      <p>Die Webseite lädt Schriften von Google Fonts; dabei wird Ihre IP-Adresse an Google übermittelt.</p>

      <h2>14. Hosting und Backups</h2>
      <p>
        Die Anwendung läuft auf Servern in Deutschland; mit dem Hoster besteht ein Auftragsverarbeitungsvertrag. Jedes
        Team hat eine eigene Datenbank. Regelmäßige Backups werden verschlüsselt innerhalb der EU gespeichert und nach
        spätestens 90 Tagen gelöscht.
      </p>

      <h2>15. Empfänger</h2>
      <ul>
        <li>Hosting-Dienstleister in Deutschland (Ziffer 14)</li>
        <li>Google und Apple, nur bei entsprechender Anmeldung bzw. Push; im Browser der Push-Dienst des jeweiligen Browsers (Ziffern 5 und 11)</li>
        <li>vom Team gewählter KI-Anbieter, nur mit hinterlegtem Schlüssel (Ziffer 7)</li>
        <li>vom Nutzer verbundene KI-Assistenten, vom Team angebundene Systeme und Webhook-Empfänger (Ziffer 8)</li>
        <li>Stripe bei kostenpflichtigen Plänen (Ziffer 12)</li>
        <li>Behörden, soweit gesetzlich vorgeschrieben</li>
      </ul>

      <h2>16. Löschung des Kontos</h2>
      <p>
        Sie können Ihr Konto jederzeit selbst löschen: in der App und im Web unter „Profil“ → „Konto löschen“, alternativ
        per E-Mail an <Mail />. Mit dem Konto werden Ihre personenbezogenen Daten und Ihr Profilbild gelöscht. Teams, in
        denen Sie das einzige Mitglied sind, werden mit allen Inhalten gelöscht. In Teams mit weiteren Mitgliedern bleiben
        Ihre Beiträge mit Ihrem Namen für das Team als Verantwortlichen erhalten.
      </p>

      <h2>17. Ihre Rechte</h2>
      <p>Sie haben das Recht auf</p>
      <ul>
        <li>Auskunft (Art. 15 DSGVO)</li>
        <li>Berichtigung (Art. 16)</li>
        <li>Löschung (Art. 17)</li>
        <li>Einschränkung der Verarbeitung (Art. 18)</li>
        <li>Datenübertragbarkeit (Art. 20)</li>
        <li>Widerspruch (Art. 21)</li>
        <li>Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft (Art. 7 Abs. 3)</li>
      </ul>
      <p>Eine formlose Nachricht an <Mail /> genügt. Betreffen Ihre Anliegen Inhalte eines Teams, leiten wir sie an das Team als Verantwortlichen weiter.</p>

      <h2>18. Beschwerderecht</h2>
      <p>
        Sie können sich bei einer Aufsichtsbehörde beschweren (Art. 77 DSGVO). Zuständig ist der Landesbeauftragte für den
        Datenschutz und die Informationsfreiheit Baden-Württemberg, Postfach 10 29 32, 70025 Stuttgart,{' '}
        <Ext href="https://www.baden-wuerttemberg.datenschutz.de">baden-wuerttemberg.datenschutz.de</Ext>.
      </p>

      <h2>19. Automatisierte Entscheidungen</h2>
      <p>Eine automatisierte Entscheidungsfindung einschließlich Profiling nach Art. 22 DSGVO findet nicht statt.</p>

      <h2>20. Änderungen</h2>
      <p>Wir passen diese Erklärung an, wenn sich die Verarbeitung ändert. Die aktuelle Fassung steht unter {SITE}/datenschutz.</p>
    </>
  );
}

function Agb() {
  return (
    <>
      <h1>Allgemeine Geschäftsbedingungen</h1>
      <p className="legal-date">Stand: {UPDATED}</p>

      <h2>§ 1 Geltungsbereich</h2>
      <p>(1) Diese AGB gelten für die Nutzung der unter {SITE} und in den Apps angebotenen Software Mach's („Mach's“) durch den Nutzer („Kunde“).</p>
      <p>(2) Anbieter ist {OPERATOR.name}, {OPERATOR.street}, {OPERATOR.city} („Anbieter“).</p>
      <p>(3) Mach's richtet sich an Unternehmer im Sinne von § 14 BGB. Mit der Registrierung eines Teams versichert der Kunde, in Ausübung seiner gewerblichen oder selbstständigen Tätigkeit zu handeln.</p>
      <p>(4) Abweichende Bedingungen des Kunden gelten nur, wenn der Anbieter ihnen ausdrücklich schriftlich zustimmt.</p>

      <h2>§ 2 Leistungen</h2>
      <p>(1) Mach's dient der Organisation von Aufgaben im Chat-Format mit Texten, Fotos, Videos, Sprachnachrichten und Dateien, der Zusammenarbeit im Team und mit externen Personen sowie optional der KI-Auswertung von Inhalten.</p>
      <p>(2) Der Funktionsumfang ergibt sich aus dem gewählten Plan und gebuchten Zusatzmodulen. Die KI-Auswertung setzt einen vom Kunden hinterlegten Schlüssel eines KI-Anbieters voraus; dessen Kosten trägt der Kunde.</p>
      <p>(3) Der Anbieter darf Mach's weiterentwickeln. Wesentliche Einschränkungen kündigt er mindestens 30 Tage vorher per E-Mail an; der Kunde kann dann zum Wirksamwerden kündigen.</p>

      <h2>§ 3 Vertragsschluss und kostenlose Nutzung</h2>
      <p>(1) Der Vertrag kommt mit der Registrierung zustande.</p>
      <p>(2) Kostenlose Pläne oder Testphasen enden ohne Verpflichtung zur Buchung eines kostenpflichtigen Plans. Neue Teams können den Plan Plus 14 Tage kostenlos testen; danach gilt der kostenlose Plan Free, sofern kein kostenpflichtiger Plan gebucht wird.</p>
      <p>(3) Die Grenzen je Plan (etwa Zahl der Mitglieder, Projekte, offenen Aufgaben, Speicher und Freigaben) ergeben sich aus der Planübersicht im Web. Wird eine Grenze erreicht, bleiben vorhandene Inhalte vollständig nutzbar; nur neue Inhalte über der Grenze sind nicht möglich, bis ein passender Plan gebucht ist oder Inhalte erledigt, archiviert oder gelöscht werden.</p>

      <h2>§ 4 Preise und Zahlung</h2>
      <p>(1) Es gelten die bei Buchung im Web angezeigten Preise zuzüglich gesetzlicher Umsatzsteuer. In den Apps werden keine Käufe angeboten.</p>
      <p>(2) Die Abrechnung erfolgt monatlich oder jährlich im Voraus über Stripe.</p>
      <p>(3) Bei Zahlungsverzug darf der Anbieter den Zugang nach Mahnung sperren; der Zahlungsanspruch bleibt bestehen.</p>
      <p>(4) Preisänderungen werden mindestens 30 Tage vorher per E-Mail angekündigt. Widerspricht der Kunde nicht und kündigt er nicht zum Wirksamwerden, gilt die Änderung als angenommen; darauf wird in der Ankündigung hingewiesen.</p>

      <h2>§ 5 Pflichten des Kunden</h2>
      <p>(1) Der Kunde hält seine Zugangsdaten geheim und meldet Missbrauch unverzüglich.</p>
      <p>(2) Der Kunde nutzt Mach's nicht für rechtswidrige Zwecke und stellt keine rechtsverletzenden Inhalte ein.</p>
      <p>(3) Der Kunde entscheidet selbst, wen er über Freigabe-Links einbindet, und verantwortet die Weitergabe dieser Links. Freigaben kann er jederzeit beenden.</p>
      <p>(4) Hinterlegt der Kunde einen KI-Schlüssel, verbindet er einen KI-Assistenten, legt er einen API-Schlüssel an oder richtet er einen Webhook ein, stellt er sicher, dass die Übermittlung der Inhalte an diesen Empfänger zulässig ist. API-Schlüssel und Signatur-Geheimnisse hält er geheim.</p>

      <h2>§ 6 Verfügbarkeit</h2>
      <p>Der Anbieter bemüht sich um hohe Verfügbarkeit, schuldet aber keine ununterbrochene Erreichbarkeit. Ein Service Level Agreement ist nur Vertragsbestandteil, wenn es gesondert vereinbart ist.</p>

      <h2>§ 7 Daten</h2>
      <p>(1) Der Anbieter erstellt regelmäßig Backups. Eine eigene Sicherung wichtiger Inhalte bleibt empfohlen.</p>
      <p>(2) Der Kunde kann jederzeit einen Export seiner Daten anfordern.</p>

      <h2>§ 8 Haftung</h2>
      <p>(1) Der Anbieter haftet unbeschränkt bei Vorsatz, grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper und Gesundheit.</p>
      <p>(2) Bei einfacher Fahrlässigkeit haftet er nur bei Verletzung wesentlicher Vertragspflichten, begrenzt auf den vertragstypisch vorhersehbaren Schaden.</p>
      <p>(3) Für Datenverlust haftet er nur in Höhe des Aufwands, der bei ordnungsgemäßer eigener Sicherung entstanden wäre.</p>
      <p>(4) Für Ergebnisse der KI-Auswertung übernimmt der Anbieter keine Gewähr; sie sind vom Kunden zu prüfen.</p>
      <p>(5) Im Übrigen ist die Haftung ausgeschlossen.</p>

      <h2>§ 9 Laufzeit und Kündigung</h2>
      <p>(1) Der Vertrag läuft auf unbestimmte Zeit. Bei monatlicher Abrechnung ist er zum Ende des Abrechnungsmonats kündbar, bei jährlicher Abrechnung zum Ende des Abrechnungsjahres.</p>
      <p>(2) Das Recht zur außerordentlichen Kündigung bleibt unberührt.</p>
      <p>(3) Kündigungen sind in Textform an <Mail /> oder in der Anwendung möglich.</p>
      <p>(4) Nach dem Ende eines kostenpflichtigen Plans gilt der Plan Free. Die Inhalte bleiben erhalten; es gelten die Grenzen nach § 3 Abs. 3.</p>

      <h2>§ 10 Datenschutz</h2>
      <p>Es gilt die <a href="/datenschutz">Datenschutzerklärung</a>. Für personenbezogene Daten, die der Anbieter im Auftrag des Kunden verarbeitet, gilt der <a href="/avv">Auftragsverarbeitungsvertrag</a>.</p>

      <h2>§ 11 Schlussbestimmungen</h2>
      <p>(1) Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts.</p>
      <p>(2) Gerichtsstand ist, soweit zulässig, der Sitz des Anbieters.</p>
      <p>(3) Sind einzelne Bestimmungen unwirksam, bleiben die übrigen wirksam.</p>
    </>
  );
}

function Avv() {
  return (
    <>
      <h1>Auftragsverarbeitungsvertrag (AVV)</h1>
      <p className="legal-date">Stand: {UPDATED}</p>
      <p>
        Dieser Vertrag konkretisiert die Pflichten nach Art. 28 DSGVO zwischen dem Kunden („Verantwortlicher“) und{' '}
        {OPERATOR.name} („Auftragsverarbeiter“) und ergänzt die <a href="/agb">AGB</a>. Er gilt mit Annahme der AGB als
        geschlossen, sobald der Verantwortliche in Mach's personenbezogene Daten Dritter verarbeitet.
      </p>

      <h2>§ 1 Gegenstand und Dauer</h2>
      <p>Gegenstand ist die Verarbeitung der Daten, die der Verantwortliche in Mach's eingibt oder hochlädt, zur Erbringung der vertraglichen Leistungen, für die Dauer des Hauptvertrags.</p>

      <h2>§ 2 Art und Zweck</h2>
      <p>Datenkategorien:</p>
      <ul>
        <li>Namen, Kontaktdaten und Profilbilder von Teammitgliedern</li>
        <li>Namen externer Personen und Protokolle ihrer Zugriffe</li>
        <li>Inhalte von Aufgaben: Texte, Fotos, Videos, Sprachnachrichten, Dateien, Bildschirmaufnahmen, Links</li>
        <li>daraus erzeugte Transkripte und Zusammenfassungen</li>
        <li>Nutzungs- und Protokolldaten</li>
      </ul>
      <p>Betroffene Personen: Mitarbeiter, Kunden, Geschäftspartner und sonstige Kontakte des Verantwortlichen sowie Personen, die in Inhalten erkennbar sind.</p>
      <p>Zweck: Bereitstellung von Mach's (Aufgabenverwaltung, Zusammenarbeit, Freigaben, Synchronisation, Suche, optionale KI-Auswertung) nach Weisung des Verantwortlichen.</p>

      <h2>§ 3 Pflichten des Auftragsverarbeiters</h2>
      <ol>
        <li>Verarbeitung nur auf dokumentierte Weisung des Verantwortlichen.</li>
        <li>Verpflichtung aller befugten Personen zur Vertraulichkeit.</li>
        <li>Technische und organisatorische Maßnahmen nach § 6.</li>
        <li>Einhaltung der Regeln für weitere Auftragsverarbeiter nach § 4.</li>
        <li>Unterstützung bei Betroffenenrechten und Datenschutzpflichten.</li>
        <li>Unverzügliche Meldung von Datenschutzverletzungen (Art. 33 DSGVO).</li>
        <li>Löschung oder Rückgabe aller Daten nach Vertragsende nach Wahl des Verantwortlichen.</li>
        <li>Bereitstellung der Informationen, die zum Nachweis der Einhaltung nötig sind.</li>
      </ol>

      <h2>§ 4 Weitere Auftragsverarbeiter</h2>
      <p>(1) Der Verantwortliche stimmt folgenden weiteren Auftragsverarbeitern zu:</p>
      <ul>
        <li><strong>Hosting-Dienstleister (Deutschland):</strong> Server-Infrastruktur, AVV vorhanden.</li>
        <li><strong>Backup-Speicher (EU):</strong> verschlüsselte Datensicherung, AVV vorhanden.</li>
        <li><strong>Stripe Payments Europe Ltd., Irland:</strong> Zahlungsabwicklung (<Ext href="https://stripe.com/de/legal/dpa">stripe.com/de/legal/dpa</Ext>).</li>
      </ul>
      <p>
        (2) Hinterlegt der Verantwortliche einen eigenen KI-Schlüssel, verbindet ein Nutzer einen KI-Assistenten oder
        richtet der Verantwortliche einen API-Schlüssel oder Webhook ein, erfolgt die Übermittlung an diesen Empfänger
        auf Weisung des Verantwortlichen im Rahmen seines eigenen Vertragsverhältnisses mit dem Empfänger; der Empfänger
        ist dann kein Unterauftragnehmer des Auftragsverarbeiters.
      </p>
      <p>(3) Über neue Unterauftragnehmer informiert der Auftragsverarbeiter mindestens 30 Tage vorher per E-Mail. Der Verantwortliche kann aus wichtigem datenschutzrechtlichen Grund widersprechen; dann besteht ein Sonderkündigungsrecht.</p>

      <h2>§ 5 Kontrollrechte</h2>
      <p>(1) Der Auftragsverarbeiter erteilt auf Anfrage Auskunft über die getroffenen Maßnahmen.</p>
      <p>(2) Prüfungen vor Ort sind nach Ankündigung mit angemessener Frist (in der Regel 30 Tage) höchstens einmal jährlich möglich, sofern kein konkreter Anlass besteht.</p>

      <h2>§ 6 Technische und organisatorische Maßnahmen</h2>
      <h3>Vertraulichkeit</h3>
      <ul>
        <li>Zutrittskontrolle durch das Rechenzentrum (Zugang nur für autorisiertes Personal)</li>
        <li>Persönliche Zugänge, Passwörter als bcrypt-Hash, signierte Anmelde-Token, Mehrfaktor-Schutz für administrative Zugänge</li>
        <li>Rollenkonzept im Team; eigene Datenbank je Team; Freigabe-Links sind zufällig, widerrufbar und auf einzelne Aufgaben oder Projekte beschränkt</li>
        <li>Verschlüsselte Übertragung (TLS), KI-Schlüssel verschlüsselt gespeichert (AES-256-GCM), verschlüsselte Backups</li>
        <li>Trennung von Produktiv- und Entwicklungsumgebung</li>
      </ul>
      <h3>Integrität</h3>
      <ul>
        <li>Serverseitige Prüfung aller Eingaben, parametrisierte Datenbankabfragen</li>
        <li>Nachvollziehbarkeit von Änderungen über den Änderungsverlauf, Protokoll der Zugriffe über Freigaben</li>
      </ul>
      <h3>Verfügbarkeit und Belastbarkeit</h3>
      <ul>
        <li>Regelmäßige automatische Backups mit Aufbewahrung von höchstens 90 Tagen, Sicherung vor jeder Aktualisierung</li>
        <li>Firewall, zeitnahe Sicherheitsupdates, Überwachung des Betriebs</li>
        <li>Offline-Fähigkeit der Apps: Eingaben gehen bei Verbindungsabbrüchen nicht verloren</li>
      </ul>
      <h3>Überprüfung</h3>
      <ul>
        <li>Verzeichnis von Verarbeitungstätigkeiten (Art. 30 DSGVO)</li>
        <li>Dokumentierter Ablauf für Datenschutzvorfälle mit Meldung binnen 72 Stunden</li>
        <li>Datenschutzfreundliche Voreinstellungen (z. B. Push und KI-Auswertung standardmäßig aus)</li>
      </ul>

      <h2>§ 7 Drittlandtransfer</h2>
      <p>Eine Übermittlung in Drittländer erfolgt nur über die in § 4 genannten Stellen und nur bei angemessenem Schutzniveau nach Art. 44 ff. DSGVO (insbesondere EU-Standardvertragsklauseln oder EU-US Data Privacy Framework).</p>

      <h2>§ 8 Vertragsende</h2>
      <p>(1) Spätestens 30 Tage nach Vertragsende werden alle Daten nach Wahl des Verantwortlichen herausgegeben oder gelöscht; ohne Wahl werden sie gelöscht.</p>
      <p>(2) Backups werden im Rahmen der regulären Rotation (höchstens 90 Tage) gelöscht.</p>
      <p>(3) Die Löschung wird auf Anforderung bestätigt.</p>

      <h2>§ 9 Haftung</h2>
      <p>Die Haftung richtet sich nach Art. 82 DSGVO; im Innenverhältnis gelten die Regelungen der AGB, soweit zwingendes Recht nicht entgegensteht.</p>

      <h2>§ 10 Schlussbestimmungen</h2>
      <p>Sind einzelne Bestimmungen unwirksam, bleiben die übrigen wirksam. Es gilt deutsches Recht.</p>
    </>
  );
}

/** Für die Stores (Google Play verlangt eine öffentliche Seite zur Kontolöschung). */
function KontoLoeschen() {
  return (
    <>
      <h1>Konto und Daten löschen</h1>
      <p>So löschst du dein Konto bei Mach's (Anbieter: {OPERATOR.name}) samt der zugehörigen Daten.</p>

      <h2>Konto selbst löschen</h2>
      <ul>
        <li><strong>In der App (iPhone, Android):</strong> unten auf „Mehr“ tippen, oben dein Profil öffnen und „Konto löschen“ wählen. Zur Sicherheit bestätigst du mit deiner E-Mail-Adresse.</li>
        <li><strong>Im Web ({SITE}):</strong> auf dein Profilbild tippen, „Konto löschen“ wählen und mit deiner E-Mail-Adresse bestätigen.</li>
      </ul>

      <h2>Löschung per E-Mail anfordern</h2>
      <p>
        Ohne Zugriff auf die App schreibst du von der E-Mail-Adresse deines Kontos an <Mail /> mit dem Betreff „Konto
        löschen“. Wir löschen das Konto innerhalb eines Monats und bestätigen dir das per E-Mail.
      </p>

      <h2>Was gelöscht wird</h2>
      <ul>
        <li>dein Konto: Name, E-Mail-Adresse, Passwort bzw. Verknüpfung mit Google oder Apple, Profilbild</li>
        <li>deine Geräte und Einstellungen: Benachrichtigungen, Verbindungen zu KI-Assistenten, von dir angelegte API-Schlüssel</li>
        <li>Teams, in denen du das einzige Mitglied bist, mit allen Inhalten: Projekte, Aufgaben, Nachrichten, Fotos, Videos, Sprachnachrichten, Dateien, Checklisten und Freigaben</li>
      </ul>

      <h2>Was erhalten bleibt</h2>
      <ul>
        <li>In Teams mit weiteren Mitgliedern bleiben deine Beiträge mit deinem Namen für das Team erhalten, weil das Team für diese Inhalte verantwortlich ist. Einzelne Inhalte kannst du vorher selbst löschen (siehe unten).</li>
        <li>Rechnungen zu einem bezahlten Tarif bewahren wir so lange auf, wie es das Steuerrecht verlangt (bis zu zehn Jahre).</li>
        <li>Sicherungskopien werden nach spätestens 90 Tagen überschrieben; bis dahin sind gelöschte Daten dort noch enthalten, werden aber nicht mehr verwendet.</li>
      </ul>

      <h2 id="inhalte">Einzelne Inhalte löschen, ohne das Konto zu löschen</h2>
      <ul>
        <li><strong>Nachrichten, Fotos, Videos, Sprachnachrichten und Dateien:</strong> an der eigenen Nachricht das Menü öffnen (in der App lange drücken) und „Löschen“ wählen. Die Datei wird dabei vom Server entfernt.</li>
        <li><strong>Profilbild:</strong> im Profil entfernen oder ersetzen.</li>
        <li><strong>Aufgaben, Checklistenpunkte, Projekte und Freigaben</strong> lassen sich in der jeweiligen Ansicht löschen bzw. beenden.</li>
        <li>Alles andere kannst du per E-Mail an <Mail /> löschen lassen.</li>
      </ul>
    </>
  );
}

function Support() {
  return (
    <>
      <h1>Hilfe und Kontakt</h1>
      <p>
        Fragen, Probleme oder Wünsche? Schreib uns an <Mail />. Wir antworten in der Regel innerhalb eines Werktags.
        Beschreib am besten kurz, auf welchem Gerät (iPhone, Android, Browser) und bei welcher Aufgabe es hakt.
      </p>

      <h2>Häufige Fragen</h2>
      <h3>Wie lade ich Kunden oder Partner ohne Konto ein?</h3>
      <p>Öffne die Aufgabe, tippe auf das Menü und wähle „Externe einladen“. Den Link schickst du per WhatsApp oder E-Mail. Für ein ganzes Projekt geht das über „Freigeben“ in der Projektauswahl.</p>
      <h3>Funktioniert Mach's ohne Internet?</h3>
      <p>Ja. Nachrichten, Fotos und Sprachnachrichten werden auf dem Gerät vorgemerkt und übertragen, sobald wieder Empfang da ist. Fehlgeschlagene Uploads lassen sich in der Nachricht erneut senden.</p>
      <h3>Wie schicke ich Inhalte aus anderen Apps?</h3>
      <p>Über „Teilen“ in Fotos, Safari oder einer anderen App und dann „Mach's“. Erscheint Mach's nicht, tippe dort auf „Mehr“ und schalte es ein.</p>
      <h3>Wie nehme ich den Bildschirm auf?</h3>
      <p>In der Aufgabe über die Büroklammer „Bildschirm aufnehmen“ wählen. Das Video landet nach dem Beenden automatisch in dieser Aufgabe.</p>
      <h3>Wie funktioniert die KI-Auswertung?</h3>
      <p>Hinterlege in den Einstellungen deinen eigenen Schlüssel von OpenAI, Anthropic oder Google. Dann werden Sprachnachrichten und Videos transkribiert und zusammengefasst. Ohne Schlüssel bleibt alles bei dir.</p>
      <h3>Wo kaufe ich einen Plan?</h3>
      <p>Pläne und Zusatzmodule gibt es nur im Web unter {SITE} in den Einstellungen unter „Tarif und Nutzung“. Es gibt Free (kostenlos), Plus und Business; neue Teams testen Plus 14 Tage kostenlos. In den Apps werden keine Käufe angeboten.</p>
      <h3>Wie lösche ich mein Konto?</h3>
      <p>Im Profil (Avatar oben rechts) ganz unten „Konto löschen“ wählen und mit deiner E-Mail-Adresse bestätigen. Teams, in denen du allein bist, werden dabei mit allen Inhalten gelöscht.</p>

      <h2>Anbieter</h2>
      <p>{OPERATOR.name}<br />{OPERATOR.street}<br />{OPERATOR.city}</p>
    </>
  );
}
